import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { StatCard } from '@/shared/ui/StatCard'
import { StatusBadge } from '@/shared/ui/Badge'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Skeleton } from '@/shared/ui/Skeleton'
import { Pagination } from '@/shared/ui/Pagination'
import { History, ArrowRight, Layers, GraduationCap, Package, Calendar, FileText, UserCog } from 'lucide-react'
import { formatCurrency } from '@/shared/utils'
import { usePagination } from '@/shared/hooks'
import { useTrainerSwapHistory } from '@/api/hooks'
import type { TrainerSwapRecord } from '@/shared/types'

function fmtDate(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return v }
}

function SwapCard({ row, onOpenBooking }: { row: TrainerSwapRecord; onOpenBooking: () => void }) {
  const studentName = row.student_name?.trim() || `Student #${row.student_id}`
  const updatedByLabel = row.approver_name?.trim() || (row.updated_by ? `Admin #${row.updated_by}` : '—')

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4 hover:shadow-md transition-shadow">
      {/* Header: student + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
            <GraduationCap size={14} className="text-brand-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{studentName}</p>
            <button onClick={onOpenBooking} className="text-[12px] text-brand-600 hover:underline font-mono">
              Booking #{row.booking_id}
            </button>
          </div>
        </div>
        <StatusBadge status={row.status} />
      </div>

      {/* Trainer change */}
      <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2.5 flex items-center justify-between gap-2 text-xs">
        <span className="font-semibold text-gray-600 truncate">{row.old_trainer?.name ?? '—'}</span>
        <ArrowRight size={13} className="text-gray-400 flex-shrink-0" />
        <span className="font-semibold text-brand-600 truncate text-right">{row.new_trainer?.name ?? '—'}</span>
      </div>

      {/* Meta */}
      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-gray-400"><Package size={11} />Package</span>
          <span className="font-medium text-gray-700 text-right truncate max-w-[60%]">{row.package_name || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-gray-400"><FileText size={11} />Reason</span>
          <span className="font-medium text-gray-700 text-right truncate max-w-[60%]" title={row.change_reason}>{row.change_reason || '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-gray-400"><UserCog size={11} />Updated By</span>
          <span className="font-medium text-gray-700 text-right truncate max-w-[60%]">{updatedByLabel}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-gray-400"><Calendar size={11} />Date</span>
          <span className="font-medium text-gray-700">{fmtDate(row.created_at)}</span>
        </div>
      </div>

      {/* Fee footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className="text-[12px] font-semibold uppercase tracking-wider text-gray-400">Fee</span>
        {row.is_free_change ? (
          <span className="px-2.5 py-0.5 rounded-full text-[12px] font-bold bg-green-50 text-green-700">Free</span>
        ) : (
          <span className="text-sm font-black text-gray-800">{formatCurrency(row.change_fee ?? 0)}</span>
        )}
      </div>
    </div>
  )
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-8 h-8 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  )
}

export function ChangeTrainerHistoryPage() {
  const navigate = useNavigate()
  const { pagination, setPagination } = usePagination({ page: 1, per_page: 20 })

  const q = useTrainerSwapHistory({ page: pagination.page, limit: pagination.per_page })
  const rows: TrainerSwapRecord[] = (q.data as any)?.data ?? []
  const meta = (q.data as any)?.meta
  const total = meta?.total ?? rows.length
  const perPage = meta?.per_page ?? pagination.per_page
  const lastPage = meta?.last_page ?? Math.max(1, Math.ceil(total / perPage))

  return (
    <div className="space-y-5">
      <PageHeader
        title="Change Trainer History"
        subtitle="Audit log of trainer reassignments across bookings"
        breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Bookings', to: '/dashboard/booking/list' }, { label: 'Change Trainer History' }]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard title="Total Swaps" value={total} icon={<History size={18} />} color="blue" />
        <StatCard title="On This Page" value={rows.length} icon={<Layers size={18} />} color="teal" />
      </div>

      {q.isError && (
        <Card><p className="text-sm text-red-500">Failed to load trainer swap history.</p></Card>
      )}

      {q.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState message="No trainer swaps found" description="There are no trainer change records yet." />
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map(row => (
              <SwapCard key={row.id} row={row} onOpenBooking={() => navigate(`/dashboard/booking/${row.booking_id}`)} />
            ))}
          </div>
          {total > perPage && (
            <Card padding={false}>
              <Pagination
                page={pagination.page}
                lastPage={lastPage}
                total={total}
                perPage={perPage}
                onPageChange={page => setPagination(prev => ({ ...prev, page }))}
              />
            </Card>
          )}
        </>
      )}
    </div>
  )
}
